/**
 * Pure windowing math for {@link WindowedTableBody} — kept separate from the
 * component so the range arithmetic is unit-testable without a DOM.
 *
 * Spacer-row virtualization (leg B of
 * operator-task-fe-logs-tab-bounded-dom-virtualization): instead of mounting one
 * `<tr>` per row for an unbounded gradual-log list, only the rows near the
 * viewport are mounted as real rows; the off-viewport rows above and below are
 * replaced by two empty full-width spacer rows whose heights reserve exactly the
 * scroll extent the unmounted rows would have occupied. The DOM `<tr>` count
 * therefore stays bounded (≈ viewport rows + overscan) regardless of how deep
 * the history is loaded, while the scrollbar still spans the full list.
 *
 * All inputs are in CSS pixels, in the scroll container's content coordinate
 * space (so `scrollTop` is the container's own `scrollTop`, and `listTop` is the
 * offset of row 0's top edge from that same scrollable content origin).
 */
export interface WindowRange {
    /** First mounted row index (inclusive). */
    start: number;
    /** One past the last mounted row index (exclusive). */
    end: number;
    /** Height (px) of the top spacer row = the unmounted rows above `start`. */
    topHeight: number;
    /** Height (px) of the bottom spacer row = the unmounted rows below `end`. */
    bottomHeight: number;
}

export interface ComputeWindowArgs {
    /** The scroll container's current `scrollTop`. */
    scrollTop: number;
    /** The scroll container's visible height (`clientHeight`). */
    viewportHeight: number;
    /** Content-space offset of row 0's top edge from the scroll content origin. */
    listTop: number;
    /** Measured (self-calibrated) uniform row height. */
    rowHeight: number;
    /** Total number of rows in the list. */
    rowCount: number;
    /** Extra rows to keep mounted above and below the viewport, to hide the
     *  mount/unmount at the edges during a fast scroll. */
    overscan: number;
}

/**
 * Compute which contiguous slice of rows to mount, plus the two spacer heights,
 * for the given scroll position. Guarantees:
 *  - `0 <= start <= end <= rowCount`;
 *  - at least one row stays mounted whenever `rowCount > 0` (so the component can
 *    keep re-measuring `rowHeight`/`listTop` from a live row even when the whole
 *    list has scrolled out of view);
 *  - `topHeight + (end-start)*rowHeight + bottomHeight === rowCount*rowHeight`
 *    (the mounted rows plus spacers always reserve the full list height, so the
 *    scrollbar does not jump).
 */
export function computeWindow({
    scrollTop,
    viewportHeight,
    listTop,
    rowHeight,
    rowCount,
    overscan,
}: ComputeWindowArgs): WindowRange {
    if (rowCount <= 0 || rowHeight <= 0 || viewportHeight <= 0) {
        return { start: 0, end: Math.max(0, rowCount), topHeight: 0, bottomHeight: 0 };
    }

    // Viewport bounds translated into list-content coordinates (row 0 top === 0).
    const relTop = scrollTop - listTop;
    const relBottom = relTop + viewportHeight;

    let start = Math.floor(relTop / rowHeight) - overscan;
    let end = Math.ceil(relBottom / rowHeight) + overscan;

    if (start < 0) start = 0;
    if (start > rowCount) start = rowCount;
    if (end > rowCount) end = rowCount;
    if (end < start) end = start;

    // Keep at least one row mounted so the component always has a live row to
    // measure from (the list may be entirely above/below the fold during a fast
    // scroll). One extra off-screen row does not meaningfully grow the DOM.
    if (end === start) {
        if (start >= rowCount) start = rowCount - 1;
        end = start + 1;
    }

    const topHeight = start * rowHeight;
    const bottomHeight = (rowCount - end) * rowHeight;
    return { start, end, topHeight, bottomHeight };
}
